import React, { useState } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  XCircle,
  CheckCircle,
  ExternalLink,
  Search,
  Filter,
  ChevronDown,
  Edit,
  RefreshCw
} from 'lucide-react';
import { dashboardStats, marketplaces, mockProducts } from '../utils/mockData';
import clsx from 'clsx';
import { TroubleshootingService } from '../services/allServices';


const severityConfig = {
  error: {
    label: 'Erro',
    color: 'red',
    icon: XCircle,
    bgClass: 'bg-red-500/10',
    borderClass: 'border-red-500/20',
    textClass: 'text-red-400',
  },
  warning: {
    label: 'Aviso',
    color: 'amber',
    icon: AlertTriangle,
    bgClass: 'bg-amber-500/10',
    borderClass: 'border-amber-500/20',
    textClass: 'text-amber-400',
  },
};


const loadErrors = async () => {
  const data = await TroubleshootingService.getErrors();
  setErrors(data.errors);
};

const ErrorItem = ({ error, onResolve }) => {
  const config = severityConfig[error.severity];
  const Icon = config.icon;
  const [isExpanded, setIsExpanded] = useState(false);

  // Tentar extrair SKU da mensagem
  const skuMatch = error.message.match(/SKU (\d+)/);
  const sku = skuMatch ? skuMatch[1] : null;

  return (
    <div className={clsx(
      'border rounded-xl overflow-hidden transition-all',
      config.bgClass,
      config.borderClass,
      isExpanded && 'shadow-xl'
    )}>
      {/* Header */}
      <div
        className="p-5 cursor-pointer hover:opacity-90 transition-opacity"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-4">
          <Icon className={clsx('w-6 h-6 flex-shrink-0 mt-0.5', config.textClass)} />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={clsx(
                    'text-xs font-semibold px-2 py-1 rounded',
                    config.bgClass,
                    config.textClass
                  )}>
                    {config.label.toUpperCase()}
                  </span>
                  <span className="text-xs text-dark-500">
                    {error.code}
                  </span>
                </div>
                <h3 className="text-dark-100 font-medium text-lg">{error.message}</h3>
              </div>
              <span className="text-dark-500 text-sm whitespace-nowrap">
                {new Date(error.timestamp).toLocaleString('pt-BR')}
              </span>
            </div>
            {error.field && (
              <p className="text-dark-400 text-sm">
                Campo afetado: <span className={config.textClass}>{error.field}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-dark-700/30">
          {/* Suggested Action */}
          <div className="pt-4">
            <h4 className="text-sm font-semibold text-dark-100 mb-3">Ação Corretiva Recomendada</h4>
            <div className="p-4 bg-dark-900/50 rounded-lg border border-dark-700/50">
              {error.code === 'DESCRIPTION_TOO_LONG' && (
                <>
                  <p className="text-dark-300 text-sm mb-3">
                    A descrição do produto excede o limite de caracteres permitido pelo marketplace.
                    Você precisa editar a descrição para caber no limite de 2000 caracteres.
                  </p>
                  <div className="flex gap-3">
                    {sku && (
                      <button className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg text-sm font-medium transition-colors">
                        <Edit className="w-4 h-4" />
                        Editar Produto {sku}
                      </button>
                    )}
                    <button className="flex items-center gap-2 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-dark-100 rounded-lg text-sm font-medium transition-colors">
                      <ExternalLink className="w-4 h-4" />
                      Ver Documentação
                    </button>
                  </div>
                </>
              )}
              {error.code === 'TOKEN_EXPIRED' && (
                <>
                  <p className="text-dark-300 text-sm mb-3">
                    O token de autenticação expirou. Você precisa reconectar sua conta do marketplace
                    para restabelecer a integração.
                  </p>
                  <button className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg text-sm font-medium transition-colors">
                    <RefreshCw className="w-4 h-4" />
                    Reconectar Marketplace
                  </button>
                </>
              )}
              {error.code === 'STOCK_MISMATCH' && (
                <>
                  <p className="text-dark-300 text-sm mb-3">
                    Há uma diferença entre o estoque registrado no sistema e o estoque no marketplace.
                    Sincronize novamente para corrigir.
                  </p>
                  <div className="flex gap-3">
                    <button className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg text-sm font-medium transition-colors">
                      <RefreshCw className="w-4 h-4" />
                      Sincronizar Estoque
                    </button>
                    {sku && (
                      <button className="flex items-center gap-2 px-4 py-2 bg-dark-700 hover:bg-dark-600 text-dark-100 rounded-lg text-sm font-medium transition-colors">
                        <Edit className="w-4 h-4" />
                        Editar Produto {sku}
                      </button>
                    )}
                  </div>
                </>
              )}
              {error.code === 'CATEGORY_NOT_MAPPED' && (
                <>
                  <p className="text-dark-300 text-sm mb-3">
                    Esta categoria ainda não foi mapeada para o marketplace selecionado.
                    Crie um mapeamento para permitir a sincronização dos produtos.
                  </p>
                  <button className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg text-sm font-medium transition-colors">
                    <Edit className="w-4 h-4" />
                    Criar Mapeamento
                  </button>
                </>
              )}
              {error.code === 'PRICE_VALIDATION' && (
                <>
                  <p className="text-dark-300 text-sm mb-3">
                    O preço está abaixo do mínimo permitido pelo marketplace.
                    Ajuste o preço para um valor aceitável.
                  </p>
                  {sku && (
                    <button className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg text-sm font-medium transition-colors">
                      <Edit className="w-4 h-4" />
                      Editar Preço do Produto {sku}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-dark-700/30">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResolve(error);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg text-sm font-medium transition-colors border border-emerald-500/20"
            >
              <CheckCircle className="w-4 h-4" />
              Marcar como Resolvido
            </button>
            <span className="text-dark-500 text-xs">ID do Erro: {error.code}-{Date.now()}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Troubleshooting() {
  const [errors, setErrors] = useState(dashboardStats.recentErrors);
  const [filter, setFilter] = useState('all'); // all, error, warning
  const [searchTerm, setSearchTerm] = useState('');

  const handleResolve = (errorToResolve) => {
    setErrors(errors.filter(e => e !== errorToResolve));
  };

  const filteredErrors = errors.filter(error => {
    const matchesSeverity = filter === 'all' || error.severity === filter;
    const matchesSearch = !searchTerm ||
      error.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      error.code.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSeverity && matchesSearch;
  });

  const errorCount = errors.filter(e => e.severity === 'error').length;
  const warningCount = errors.filter(e => e.severity === 'warning').length;

  return (
    <div className="p-8 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-dark-100 mb-2">
            Central de Troubleshooting
          </h1>
          <p className="text-dark-400">
            Identifique e resolva problemas de sincronização
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg font-medium transition-colors shadow-lg shadow-primary-500/20">
          <RefreshCw className="w-5 h-5" />
          Sincronizar Tudo
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-dark-700/50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-dark-100" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">{errorCount}</p>
              <p className="text-dark-400 text-sm">Erros Críticos</p>
            </div>
          </div>
        </div>
        <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-dark-700/50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-dark-100" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">{warningCount}</p>
              <p className="text-dark-400 text-sm">Avisos</p>
            </div>
          </div>
        </div>
        <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-dark-700/50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-dark-100" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">
                {((682000 / 918072) * 100).toFixed(1)}%
              </p>
              <p className="text-dark-400 text-sm">Taxa de Sucesso</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-500" />
          <input
            type="text"
            placeholder="Pesquisar por mensagem de erro ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-dark-800/50 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={clsx(
              'px-4 py-3 rounded-lg font-medium transition-all',
              filter === 'all'
                ? 'bg-primary-500 text-dark-100 shadow-lg shadow-primary-500/20'
                : 'bg-dark-800/50 border border-dark-700 text-dark-300 hover:text-dark-100 hover:border-dark-600'
            )}
          >
            Todos ({errors.length})
          </button>
          <button
            onClick={() => setFilter('error')}
            className={clsx(
              'px-4 py-3 rounded-lg font-medium transition-all',
              filter === 'error'
                ? 'bg-red-500 text-dark-100 shadow-lg shadow-red-500/20'
                : 'bg-dark-800/50 border border-dark-700 text-dark-300 hover:text-dark-100 hover:border-dark-600'
            )}
          >
            Erros ({errorCount})
          </button>
          <button
            onClick={() => setFilter('warning')}
            className={clsx(
              'px-4 py-3 rounded-lg font-medium transition-all',
              filter === 'warning'
                ? 'bg-amber-500 text-dark-100 shadow-lg shadow-amber-500/20'
                : 'bg-dark-800/50 border border-dark-700 text-dark-300 hover:text-dark-100 hover:border-dark-600'
            )}
          >
            Avisos ({warningCount})
          </button>
        </div>
      </div>

      {/* Errors List */}
      <div className="space-y-4">
        {filteredErrors.length > 0 ? (
          filteredErrors.map((error, index) => (
            <ErrorItem
              key={`${error.code}-${index}`}
              error={error}
              onResolve={handleResolve}
            />
          ))
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-xl font-semibold text-dark-100 mb-2">
              {searchTerm || filter !== 'all'
                ? 'Nenhum resultado encontrado'
                : 'Tudo funcionando perfeitamente!'}
            </h3>
            <p className="text-dark-400">
              {searchTerm || filter !== 'all'
                ? 'Tente ajustar seus filtros de pesquisa'
                : 'Não há erros ou avisos no momento'}
            </p>
          </div>
        )}
      </div>

      {/* Error Type Distribution */}
      {filteredErrors.length > 0 && (
        <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-dark-700/50">
          <h3 className="text-lg font-semibold text-dark-100 mb-4">Distribuição por Tipo de Erro</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...new Set(errors.map(e => e.code))].map(code => {
              const count = errors.filter(e => e.code === code).length;
              return (
                <div key={code} className="p-4 bg-dark-900/30 rounded-lg border border-dark-700/30">
                  <p className="text-2xl font-bold text-dark-100 mb-1">{count}</p>
                  <p className="text-dark-400 text-sm">{code}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
